<template>
  <Transition name="modal">
    <div
      v-if="props.options.showModal"
      class="modal-mask"
      id="modalSubscribeExitEmail"
      @click.self="close"
      @keydown.esc="close"
      tabindex="-1"
      ref="root"
    >
      <div class="modal-container">
        <button class="close-btn" type="button" @click="close" aria-label="Fechar">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>

        <div class="modal-body">
          <img
            v-if="props.options.image"
            :src="props.options.image"
            alt=""
            class="hero"
          />

          <div v-if="!sent" class="content-wrap">
            <div class="content" v-html="sanitizedHtml"></div>

            <form @submit.prevent="onSubmit" novalidate class="form">
              <input
                v-model="website"
                name="website"
                type="text"
                tabindex="-1"
                autocomplete="off"
                class="honeypot"
                aria-hidden="true"
              />

              <input
                v-model="email"
                type="email"
                required
                class="email-input"
                :class="{ 'has-error': errorMessage }"
                :placeholder="props.options.inputs?.email_placeholder || 'Seu e-mail'"
                :disabled="submitting"
                autocomplete="email"
              />

              <label class="consent">
                <input type="checkbox" v-model="consent" :disabled="submitting" class="consent-checkbox" />
                <span class="consent-box" aria-hidden="true">
                  <svg v-if="consent" viewBox="0 0 16 16" width="12" height="12">
                    <path d="M3 8.5L6.5 12L13 4.5" stroke="#fff" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
                <span class="consent-text">{{ consentText }}</span>
              </label>

              <button
                type="submit"
                class="cta"
                :style="ctaStyle"
                :disabled="submitting || !canSubmit"
              >
                <span v-if="!submitting">{{ props.options.cta?.label || 'Enviar' }}</span>
                <span v-else>{{ sendingText }}</span>
              </button>

              <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
            </form>
          </div>

          <div v-else class="success">
            <svg width="56" height="56" viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <circle cx="24" cy="24" r="22" stroke="#2fb170" stroke-width="3" />
              <path d="M14 24L21 31L34 18" stroke="#2fb170" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <p>{{ successText }}</p>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useSubscribeExitCapture } from '@/composables/useSubscribeExitCapture';

const props = defineProps<{
  options: {
    showModal: boolean;
    subscribeExitId: number;
    image: string;
    html: string;
    inputs?: { email_placeholder?: string };
    cta?: { label?: string; bg_color?: string; text_color?: string };
    locale: string;
  };
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'submitted', payload: { campaignId: number; email: string }): void;
}>();

const root = ref<HTMLElement | null>(null);
const email = ref('');
const consent = ref(false);
const website = ref('');
const submitting = ref(false);
const sent = ref(false);
const errorMessage = ref('');

const isEn = computed(() => props.options.locale === 'en');
const consentText = computed(() =>
  isEn.value
    ? 'I agree to receive promotional emails and accept the privacy terms.'
    : 'Aceito receber e-mails promocionais e concordo com os termos de privacidade.'
);
const sendingText = computed(() => (isEn.value ? 'Sending…' : 'Enviando…'));
const successText = computed(() =>
  isEn.value ? 'Check your inbox!' : 'Verifique sua caixa de entrada em instantes.'
);

const canSubmit = computed(() => {
  if (!email.value) return false;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value);
  return emailOk && consent.value;
});

const ctaStyle = computed(() => ({
  backgroundColor: props.options.cta?.bg_color || '#d6336c',
  color: props.options.cta?.text_color || '#ffffff',
}));

// Strip inline font-size/font-family from TinyMCE-generated HTML so the modal
// renders with our own typography regardless of what the admin chose.
// Bold/italic and heading tags are preserved.
const sanitizedHtml = computed(() => {
  const raw = props.options.html || '';
  return raw
    .replace(/font-size\s*:\s*[^;"']+;?/gi, '')
    .replace(/font-family\s*:\s*[^;"']+;?/gi, '')
    .replace(/line-height\s*:\s*[^;"']+;?/gi, '');
});

const { submit } = useSubscribeExitCapture();

async function onSubmit() {
  if (website.value) return;
  if (!canSubmit.value) {
    errorMessage.value = isEn.value
      ? 'Please enter a valid e-mail and accept the terms.'
      : 'Informe um e-mail válido e aceite os termos.';
    return;
  }
  errorMessage.value = '';
  submitting.value = true;

  const normalizedEmail = email.value.trim().toLowerCase();
  email.value = normalizedEmail;

  const result = await submit({
    subscribe_exit_id: props.options.subscribeExitId,
    email: normalizedEmail,
    locale: props.options.locale || 'pt-br',
    consent: consent.value,
    website: website.value,
  });

  submitting.value = false;

  if (result.ok) {
    sent.value = true;
    emit('submitted', {
      campaignId: props.options.subscribeExitId,
      email: normalizedEmail,
    });
    setTimeout(() => {
      emit('close');
    }, 3000);
  } else {
    errorMessage.value = result.message;
  }
}

function close() {
  emit('close');
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close();
}

watch(
  () => props.options.showModal,
  async (isOpen) => {
    if (isOpen) {
      errorMessage.value = '';
      sent.value = false;
      email.value = '';
      consent.value = false;
      website.value = '';
      await nextTick();
      root.value?.focus();
      window.addEventListener('keydown', onKeydown);
    } else {
      window.removeEventListener('keydown', onKeydown);
    }
  }
);

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  inset: 0;
  background-color: rgba(15, 20, 30, 0.65);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  padding: 24px;
  overflow-y: auto;
  transition: opacity 0.3s ease;
  outline: none;
}

.modal-container {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 48px);
  margin: auto;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  transition: transform 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.92);
  color: #333;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.15s ease, transform 0.15s ease;
}

.close-btn:hover {
  background: #fff;
  transform: scale(1.05);
}

.modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow-y: auto;
  min-height: 0;
}

.hero {
  width: 100%;
  max-height: 240px;
  height: auto;
  object-fit: cover;
  display: block;
}

.content-wrap {
  width: 100%;
  padding: 32px 32px 36px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 32px;
}

.content {
  font-size: 16px;
  line-height: 1.6;
  color: #2b2b2b;
  width: 100%;
}

.content :deep(*) {
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
  background: transparent !important;
  max-width: 100%;
}

.content :deep(h1),
.content :deep(h2) {
  font-size: 24px !important;
  line-height: 1.3 !important;
  margin: 0 0 10px !important;
}

.content :deep(h3),
.content :deep(h4),
.content :deep(h5),
.content :deep(h6) {
  font-size: 18px !important;
  line-height: 1.3 !important;
  margin: 0 0 8px !important;
}

.content :deep(p) {
  margin: 0 0 8px !important;
}

.content :deep(p:last-child),
.content :deep(*:last-child) {
  margin-bottom: 0 !important;
}

.content :deep(ul),
.content :deep(ol) {
  margin: 0 0 8px !important;
  padding-left: 20px;
}

.content :deep(img) {
  max-width: 100%;
  height: auto;
}

.form {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 22px;
}

.honeypot {
  position: absolute;
  left: -9999px;
  top: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
}

.email-input {
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #dadfe4;
  border-radius: 4px;
  font-size: 13.5px;
  color: #1a1a1a;
  background: #fff;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  font-family: inherit;
}

.email-input::placeholder {
  color: #9aa0a6;
}

.email-input:focus {
  outline: none;
  border-color: #777;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}

.email-input.has-error {
  border-color: #d64545;
}

.consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: #55606b;
  cursor: pointer;
  text-align: left;
}

.consent-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.consent-box {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 1.5px solid #c7cdd3;
  border-radius: 4px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;
  box-sizing: border-box;
}

.consent:hover .consent-box {
  border-color: #9aa0a6;
}

.consent-checkbox:checked + .consent-box {
  background: #b20000;
  border-color: #b20000;
}

.consent-checkbox:focus-visible + .consent-box {
  box-shadow: 0 0 0 3px rgba(178, 0, 0, 0.25);
}

.consent-text {
  flex: 1;
}

.cta {
  display: block;
  width: 100%;
  padding: 14px;
  border: 0;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.05s ease;
  font-family: inherit;
}

.cta:hover:not(:disabled) {
  filter: brightness(0.92);
}

.cta:active:not(:disabled) {
  transform: translateY(1px);
}

.cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error {
  color: #d64545;
  margin: 12px 0 0;
  font-size: 13px;
  text-align: center;
}

.success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 56px 24px;
  text-align: center;
  width: 100%;
}

.success p {
  margin: 0;
  font-size: 17px;
  color: #2fb170;
  font-weight: 500;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95);
}

@media (max-width: 560px) {
  .modal-mask {
    padding: 12px;
  }
  .modal-container {
    max-height: calc(100vh - 24px);
  }
  .hero {
    max-height: 180px;
  }
  .content-wrap {
    padding: 20px 18px 24px;
    gap: 22px;
  }
}
</style>
